import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  EventAvailableRounded,
  PaymentRounded,
  InfoRounded,
  NotificationsRounded,
  NotificationsOffRounded,
  MarkEmailReadRounded,
  DeleteRounded,
} from '@mui/icons-material';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { isToday, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import PageShell from '../../components/shared/PageShell';
import { RADIUS } from '../../theme/tokens';
import SectionHeader from '../../components/shared/SectionHeader';
import GlassCard from '../../components/shared/GlassCard';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { duration, easing, listContainerVariants, listItemVariants } from '../../theme/motion';

const MotionBox = motion(Box);

// --- Helpers ---

function getNotificationIcon(type) {
  switch (type) {
    case 'booking':
      return <EventAvailableRounded sx={{ fontSize: 20, color: 'primary.main' }} />;
    case 'payment':
      return <PaymentRounded sx={{ fontSize: 20, color: 'secondary.main' }} />;
    case 'info':
      return <InfoRounded sx={{ fontSize: 20, color: 'info.main' }} />;
    default:
      return <NotificationsRounded sx={{ fontSize: 20, color: 'text.secondary' }} />;
  }
}

function formatTimestamp(dateStr) {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return formatDistanceToNowStrict(date, { addSuffix: false });
  } catch {
    return '';
  }
}

// --- Swipeable notification row ---

const SWIPE_THRESHOLD = -80;

function NotificationRow({ notification, onMarkRead, onDelete, reducedMotion, isDesktop }) {
  const x = useMotionValue(0);
  // Fade in the delete-reveal background as the card slides left
  const deleteBgOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const isDragging = useRef(false);

  const handleDragEnd = useCallback((_, info) => {
    if (info.offset.x < SWIPE_THRESHOLD) {
      animate(x, -320, { duration: duration.fast });
      onDelete(notification.id);
    } else {
      animate(x, 0, easing.spring.snappy);
    }
    isDragging.current = false;
  }, [notification.id, onDelete, x]);

  const handleRowClick = useCallback(() => {
    if (isDragging.current) return;
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }
  }, [notification.id, notification.is_read, onMarkRead]);

  const rowContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 2,
        cursor: 'pointer',
        position: 'relative',
      }}
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(); }}
      aria-label={
        notification.is_read
          ? notification.title
          : `Unread notification: ${notification.title}`
      }
    >
      {/* Unread tone dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: notification.is_read ? 'transparent' : 'primary.main',
          mt: 0.75,
          flexShrink: 0,
        }}
        aria-hidden="true"
      />

      {/* Type icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: RADIUS.md,
          bgcolor: 'background.subtle',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {getNotificationIcon(notification.type)}
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={notification.is_read ? 400 : 700}
          color="text.primary"
          noWrap
        >
          {notification.title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {notification.message}
        </Typography>
      </Box>

      {/* Timestamp + desktop delete */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" color="text.tertiary">
          {formatTimestamp(notification.created_at)}
        </Typography>
        {isDesktop && (
          <IconButton
            size="small"
            aria-label="Delete notification"
            onClick={e => { e.stopPropagation(); onDelete(notification.id); }}
            sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
          >
            <DeleteRounded sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  if (reducedMotion || isDesktop) {
    return (
      <GlassCard
        sx={{
          mb: 1,
          bgcolor: notification.is_read ? 'background.paper' : 'background.subtle',
        }}
      >
        {rowContent}
      </GlassCard>
    );
  }

  // Mobile swipe-to-dismiss
  return (
    <Box sx={{ position: 'relative', mb: 1, overflow: 'hidden', borderRadius: RADIUS.md }}>
      {/* Delete reveal layer behind the card */}
      <MotionBox
        style={{ opacity: deleteBgOpacity }}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'error.main',
          borderRadius: RADIUS.md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pr: 2,
        }}
        aria-hidden="true"
      >
        <DeleteRounded sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 22 }} />
      </MotionBox>

      <MotionBox
        style={{ x }}
        drag="x"
        dragConstraints={{ left: -120, right: 0 }}
        dragElastic={{ left: 0.1, right: 0 }}
        onDragStart={() => { isDragging.current = true; }}
        onDragEnd={handleDragEnd}
        sx={{ position: 'relative', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
      >
        <GlassCard
          sx={{
            bgcolor: notification.is_read ? 'background.paper' : 'background.subtle',
          }}
        >
          {rowContent}
        </GlassCard>
      </MotionBox>
    </Box>
  );
}

// --- Section grouping ---

function NotificationSection({ title, notifications, onMarkRead, onDelete, reducedMotion, isDesktop }) {
  const containerMotion = reducedMotion
    ? {}
    : { variants: listContainerVariants, initial: 'initial', animate: 'animate' };

  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader title={title} />
      <MotionBox {...containerMotion}>
        <AnimatePresence>
          {notifications.map(notification => (
            <MotionBox
              key={notification.id}
              variants={reducedMotion ? undefined : listItemVariants}
              exit={{ opacity: 0, x: -40, transition: { duration: duration.fast } }}
            >
              <NotificationRow
                notification={notification}
                onMarkRead={onMarkRead}
                onDelete={onDelete}
                reducedMotion={reducedMotion}
                isDesktop={isDesktop}
              />
            </MotionBox>
          ))}
        </AnimatePresence>
      </MotionBox>
    </Box>
  );
}

// --- Page component ---

function StudentNotifications() {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotionPreferred();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count'),
      ]);
      setNotifications(notifRes.data.data.notifications || []);
      setUnreadCount(countRes.data.data.unread_count || 0);
    } catch (err) {
      setFetchError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkRead = useCallback(async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silent — non-critical
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      setMarkAllLoading(true);
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      setToast({ open: true, message: 'All notifications marked as read', severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Failed to mark all as read', severity: 'error' });
    } finally {
      setMarkAllLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async (notificationId) => {
    const deleted = notifications.find(n => n.id === notificationId);
    // Optimistic remove
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (!deleted?.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    try {
      await api.delete(`/notifications/${notificationId}`);
    } catch {
      // Restore on failure
      setNotifications(prev => {
        const exists = prev.find(n => n.id === notificationId);
        if (exists || !deleted) return prev;
        return [deleted, ...prev].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      });
      if (!deleted?.is_read) {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications]);

  // Preserved from original — clears all read notifications via the API.
  // eslint-disable-next-line no-unused-vars
  const handleClearRead = useCallback(async () => {
    try {
      await api.delete('/notifications/clear-read');
      setNotifications(prev => prev.filter(n => !n.is_read));
    } catch {
      // silent
    }
  }, []);

  // Group into Today / Earlier
  const todayNotifications = notifications.filter(n => {
    try { return isToday(parseISO(n.created_at)); } catch { return false; }
  });
  const earlierNotifications = notifications.filter(n => {
    try { return !isToday(parseISO(n.created_at)); } catch { return true; }
  });

  const markAllAction = (
    <Button
      size="small"
      startIcon={
        markAllLoading
          ? <CircularProgress size={14} color="inherit" />
          : <MarkEmailReadRounded sx={{ fontSize: 16 }} />
      }
      onClick={handleMarkAllRead}
      disabled={unreadCount === 0 || markAllLoading}
      aria-label="Mark all notifications as read"
      sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
    >
      Mark all read
    </Button>
  );

  if (loading) {
    return (
      <PageShell title="Notifications" backTo={-1}>
        <LoadingSkeleton variant="listItem" count={5} />
      </PageShell>
    );
  }

  if (fetchError) {
    return (
      <PageShell title="Notifications" backTo={-1}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchNotifications}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell title="Notifications" backTo={-1} action={markAllAction}>
      {notifications.length === 0 ? (
        <EmptyState
          icon={<NotificationsOffRounded />}
          title="You're all caught up"
          description="No notifications right now. Check back later."
        />
      ) : (
        <>
          {todayNotifications.length > 0 && (
            <NotificationSection
              title="Today"
              notifications={todayNotifications}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              reducedMotion={reducedMotion}
              isDesktop={isDesktop}
            />
          )}
          {earlierNotifications.length > 0 && (
            <NotificationSection
              title="Earlier"
              notifications={earlierNotifications}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              reducedMotion={reducedMotion}
              isDesktop={isDesktop}
            />
          )}
        </>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast.open && (
          <MotionBox
            key="toast"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: duration.fast, ease: easing.standard }}
            sx={{
              position: 'fixed',
              bottom: 96,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2000,
              minWidth: 260,
            }}
          >
            <Alert
              severity={toast.severity}
              onClose={() => setToast(t => ({ ...t, open: false }))}
              sx={{ borderRadius: RADIUS.md, boxShadow: 6 }}
            >
              {toast.message}
            </Alert>
          </MotionBox>
        )}
      </AnimatePresence>
    </PageShell>
  );
}

export default StudentNotifications;
